import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import type { ResponsiveStyleProps, SpacingValue, StyleProps } from "@/lib/craft/style-props";
import { defaultStyleProps, emptySpacing } from "@/lib/craft/style-props";
import type { Breakpoint } from "@/lib/craft/style-props";
import { useSelectedBlockProps } from "./useBlockSettings";

type StyledBlockProps = { style?: ResponsiveStyleProps };

export function useStyledBlock<T extends StyledBlockProps>() {
  const { props, setProp } = useSelectedBlockProps<T & StyledBlockProps>();
  const style = props.style ?? defaultStyleProps();

  function updateStyle(breakpoint: Breakpoint, patch: Partial<StyleProps>) {
    setProp((p) => {
      const current = p.style ?? defaultStyleProps();
      if (breakpoint === "desktop") {
        p.style = { ...current, desktop: { ...current.desktop, ...patch } };
      } else {
        p.style = {
          ...current,
          [breakpoint]: { ...(current[breakpoint] ?? {}), ...patch },
        };
      }
    });
  }

  return { props, setProp, style, updateStyle };
}

function SpacingInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: SpacingValue;
  onChange: (v: SpacingValue) => void;
}) {
  const v = value ?? emptySpacing();
  const linked = v.linked !== false;

  function setAll(n: number) {
    onChange({ top: n, right: n, bottom: n, left: n, linked: true });
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs">{label}</Label>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-muted-foreground">Link</span>
          <Switch
            checked={linked}
            onCheckedChange={(checked) =>
              onChange(checked ? { ...v, linked: true } : { ...v, linked: false })
            }
            className="scale-75"
          />
        </div>
      </div>
      {linked ? (
        <Input
          type="number"
          className="h-8 text-xs"
          value={v.top ?? 0}
          onChange={(e) => setAll(Number(e.target.value) || 0)}
        />
      ) : (
        <div className="grid grid-cols-2 gap-1">
          {(["top", "right", "bottom", "left"] as const).map((side) => (
            <Input
              key={side}
              type="number"
              className="h-8 text-xs"
              placeholder={side[0].toUpperCase()}
              value={v[side] ?? 0}
              onChange={(e) =>
                onChange({ ...v, [side]: Number(e.target.value) || 0, linked: false })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function StyleControls({
  style,
  updateStyle,
  breakpoint = "desktop",
  showTypography = false,
  showBackground = true,
  showBorderRadius = false,
}: {
  style: ResponsiveStyleProps;
  updateStyle: (bp: Breakpoint, patch: Partial<StyleProps>) => void;
  breakpoint?: Breakpoint;
  showTypography?: boolean;
  showBackground?: boolean;
  showBorderRadius?: boolean;
}) {
  const s =
    breakpoint === "desktop"
      ? style.desktop
      : { ...style.desktop, ...(breakpoint === "tablet" ? style.tablet : style.mobile) };

  return (
    <div className="space-y-3">
      <SpacingInput
        label="Padding"
        value={s.padding}
        onChange={(padding) => updateStyle(breakpoint, { padding })}
      />
      <SpacingInput
        label="Margin"
        value={s.margin}
        onChange={(margin) => updateStyle(breakpoint, { margin })}
      />
      {showBackground && (
        <>
          <div className="space-y-1">
            <Label className="text-xs">Background color</Label>
            <Input
              type="color"
              className="h-8 w-full cursor-pointer p-1"
              value={s.backgroundColor ?? "#ffffff"}
              onChange={(e) => updateStyle(breakpoint, { backgroundColor: e.target.value })}
            />
          </div>
        </>
      )}
      <div className="space-y-1">
        <Label className="text-xs">Text align</Label>
        <Select
          value={s.textAlign ?? "left"}
          onValueChange={(v) =>
            updateStyle(breakpoint, { textAlign: v as StyleProps["textAlign"] })
          }
        >
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="left">Left</SelectItem>
            <SelectItem value="center">Center</SelectItem>
            <SelectItem value="right">Right</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {showTypography && (
        <>
          <div className="space-y-1">
            <Label className="text-xs">Font size (px)</Label>
            <Input
              type="number"
              className="h-8 text-xs"
              value={s.fontSize ?? 16}
              onChange={(e) => updateStyle(breakpoint, { fontSize: Number(e.target.value) || 16 })}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Font weight</Label>
            <Select
              value={String(s.fontWeight ?? 400)}
              onValueChange={(v) => updateStyle(breakpoint, { fontWeight: Number(v) })}
            >
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="400">Normal</SelectItem>
                <SelectItem value="500">Medium</SelectItem>
                <SelectItem value="600">Semibold</SelectItem>
                <SelectItem value="700">Bold</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Text color</Label>
            <Input
              type="color"
              className="h-8 w-full cursor-pointer p-1"
              value={s.color ?? "#1a1a1a"}
              onChange={(e) => updateStyle(breakpoint, { color: e.target.value })}
            />
          </div>
        </>
      )}
      {showBorderRadius && (
        <div className="space-y-1">
          <Label className="text-xs">Border radius ({s.borderRadius ?? 0}px)</Label>
          <Slider
            value={[s.borderRadius ?? 0]}
            max={48}
            step={1}
            onValueChange={([v]) => updateStyle(breakpoint, { borderRadius: v })}
          />
        </div>
      )}
      <div className="space-y-1">
        <Label className="text-xs">Custom CSS class</Label>
        <Input
          className="h-8 font-mono text-xs"
          value={s.customClass ?? ""}
          onChange={(e) => updateStyle(breakpoint, { customClass: e.target.value })}
          placeholder="my-class"
        />
      </div>
    </div>
  );
}

export function AdvancedStyleControls({
  style,
  updateStyle,
  breakpoint = "desktop",
}: {
  style: ResponsiveStyleProps;
  updateStyle: (bp: Breakpoint, patch: Partial<StyleProps>) => void;
  breakpoint?: Breakpoint;
}) {
  const s =
    breakpoint === "desktop"
      ? style.desktop
      : { ...style.desktop, ...(breakpoint === "tablet" ? style.tablet : style.mobile) };

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label className="text-xs">Box shadow</Label>
        <Input
          className="h-8 font-mono text-xs"
          value={s.boxShadow ?? ""}
          onChange={(e) => updateStyle(breakpoint, { boxShadow: e.target.value })}
          placeholder="0 4px 12px rgba(0,0,0,0.1)"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">Border width</Label>
          <Input
            type="number"
            className="h-8 text-xs"
            value={s.borderWidth ?? 0}
            onChange={(e) => updateStyle(breakpoint, { borderWidth: Number(e.target.value) || 0 })}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Border color</Label>
          <Input
            type="color"
            className="h-8 w-full cursor-pointer p-1"
            value={s.borderColor ?? "#cccccc"}
            onChange={(e) => updateStyle(breakpoint, { borderColor: e.target.value })}
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Custom CSS (scoped)</Label>
        <textarea
          className="min-h-[72px] w-full rounded border bg-background px-2 py-1 font-mono text-xs"
          value={s.customCss ?? ""}
          onChange={(e) => updateStyle(breakpoint, { customCss: e.target.value })}
          placeholder=".craft-node { }"
        />
      </div>
    </div>
  );
}
